import { Formik } from 'formik';
import { FormikErrors } from 'formik';
import { flattenDeep, isString } from 'lodash';
import capitalize from 'lodash/capitalize';
import React, { useEffect, useState } from 'react';

import NUXForm from '@components/NUXForm';
import { useProfile, useProfileUpdate } from '@hooks';
import {
  COMPANY_SIZE_OPTIONS,
  JOB_FUNCTION_OPTIONS,
  JOB_LEVEL_OPTIONS,
  ORGANIZATION_INDUSTRY_OPTIONS,
  PROFILE_FIELD_OTHER_VALUE,
  PROFILE_OTHER_OPTION,
} from '@utils/backend-api/profileSettings';
import { FirstPartyDataProps } from '@utils/backend-api/userProfile';
import { BLANK_OPTION, BLANK_RECORD_OPTION } from '@utils/constants';
import { AutocompleteRecord } from '@utils/interfaces';

import SubIndustryList from './ProfileFields/SubIndustryList';
import SubJobFunctionList from './ProfileFields/SubJobFunctionList';
import StepContainer from './StepContainer';
import schema from './schema';

interface StepProfileProps {
  currentUserId: number;
  heading: string;
  nextStep: () => void;
  subheading?: string;
  continueEvent?: ({ payload }: { payload: object }) => void;
}

export interface StepProfileFormProps extends FirstPartyDataProps {
  company: AutocompleteRecord;
  firstName: string;
  jobTitle?: AutocompleteRecord;
  lastName: string;
}

const INITIAL_STATE = {
  company: BLANK_RECORD_OPTION,
  companySize: BLANK_OPTION,
  customIndustry: BLANK_RECORD_OPTION,
  customJobFunction: BLANK_RECORD_OPTION,
  customJobLevel: BLANK_RECORD_OPTION,
  customSubIndustry: BLANK_RECORD_OPTION,
  customSubJobFunction: BLANK_RECORD_OPTION,
  firstName: '',
  industry: BLANK_OPTION,
  jobFunction: BLANK_OPTION,
  jobLevel: BLANK_OPTION,
  jobTitle: BLANK_RECORD_OPTION,
  lastName: '',
  postalCode: '',
  subIndustry: BLANK_OPTION,
  subJobFunction: BLANK_OPTION,
};

const StepProfile = ({
  currentUserId,
  heading,
  nextStep,
  subheading,
  continueEvent,
}: StepProfileProps) => {
  const [initialValues, setInitialValues] = useState(INITIAL_STATE);
  const { saveProfile, error } = useProfileUpdate({ userId: currentUserId });
  const { profile, isLoadingProfile } = useProfile();

  const handleSubmit = async (values) => {
    const sanitizedParams = {
      name: `${values.firstName} ${values.lastName}` || '',
      company_name: values.company?.label || '',
      title_at_company: values.jobTitle?.label || '',
      job_level:
        values.jobLevel.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customJobLevel.label
          : values.jobLevel.value,
      job_function:
        values.jobFunction.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customJobFunction.label
          : values.jobFunction.value,
      sub_job_function:
        values.subJobFunction.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customSubJobFunction.label
          : values.subJobFunction.value,
      organization_industry:
        values.industry.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customIndustry.label
          : values.industry.value,
      organization_sub_industry:
        values.subIndustry.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customSubIndustry.label
          : values.subIndustry.value,

      company_size: values.companySize.value,
      postal_code: values.postalCode,
      profile_enabled: true,
    };
    saveProfile(sanitizedParams);
    if (continueEvent) continueEvent({ payload: sanitizedParams });
    nextStep();
  };

  useEffect(() => {
    const getSelectedOptions = (possibleOptions, profileKey) => {
      const selectedStandardValue = profile?.[profileKey];
      const standardOption =
        possibleOptions?.find((item) => item.value === selectedStandardValue) || BLANK_OPTION;
      const customOption = BLANK_RECORD_OPTION;

      if (selectedStandardValue && !standardOption.label) {
        return {
          standardOption: PROFILE_OTHER_OPTION,
          customOption: { label: capitalize(selectedStandardValue), value: selectedStandardValue },
        };
      }

      return { standardOption, customOption };
    };

    if (!isLoadingProfile) {
      const name = profile?.name?.split(' ');
      const companySize = COMPANY_SIZE_OPTIONS.find(
        (item) => item.value === profile?.companySize
      ) || { ...BLANK_OPTION };

      const industryValueAndCustomValue = getSelectedOptions(
        ORGANIZATION_INDUSTRY_OPTIONS,
        'organizationIndustry'
      );
      const jobFunctionValueAndCustomValue = getSelectedOptions(
        JOB_FUNCTION_OPTIONS,
        'jobFunction'
      );
      const jobLevelValueAndCustomValue = getSelectedOptions(JOB_LEVEL_OPTIONS, 'jobLevel');
      const jobFunction = JOB_FUNCTION_OPTIONS.find((item) => item.value === profile?.jobFunction);
      const industry = ORGANIZATION_INDUSTRY_OPTIONS.find(
        (item) => item.value === profile?.organizationIndustry
      );
      const subJobFunctionValueAndCustomValue = getSelectedOptions(
        SubJobFunctionList[jobFunction?.label],
        'subJobFunction'
      );
      const subIndustryValueAndCustomValue = getSelectedOptions(
        SubIndustryList[industry?.label],
        'organizationSubIndustry'
      );

      setInitialValues({
        company: profile.company
          ? { label: capitalize(profile.company.label), value: profile.company.value }
          : BLANK_RECORD_OPTION,
        companySize: companySize,
        customIndustry: industryValueAndCustomValue.customOption,
        customJobFunction: jobFunctionValueAndCustomValue.customOption,
        customJobLevel: jobLevelValueAndCustomValue.customOption,
        customSubIndustry: subIndustryValueAndCustomValue.customOption,
        customSubJobFunction: subJobFunctionValueAndCustomValue.customOption,
        firstName: name[0] === 'Guest' ? '' : name[0] || '',
        industry: industryValueAndCustomValue.standardOption,
        jobFunction: jobFunctionValueAndCustomValue.standardOption,
        jobLevel: jobLevelValueAndCustomValue.standardOption,
        jobTitle: profile.titleAtCompany || { label: '', value: 0 },
        lastName: name[1] || '',
        postalCode: profile.postalCode || '',
        subIndustry: subIndustryValueAndCustomValue.standardOption,
        subJobFunction: subJobFunctionValueAndCustomValue.standardOption,
      });
    }
  }, [
    isLoadingProfile,
    profile,
    profile?.jobFunction,
    profile?.jobLevel,
    profile?.subJobFunction,
    profile?.organizationIndustry,
    profile?.organizationSubIndustry,
    profile?.organizationSize,
    profile?.postalCode,
  ]);

  const flattenErrors = <T,>(errors: FormikErrors<T>): string[] => {
    const getStrings = (value: unknown): string[] => {
      if (isString(value)) return [value];
      if (Array.isArray(value)) return flattenDeep(value.map(getStrings));
      if (value && typeof value === 'object')
        return flattenDeep(Object.values(value).map(getStrings));
      return [];
    };

    return getStrings(errors).filter(Boolean);
  };

  if (isLoadingProfile) return null;
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {(formik) => (
        <StepContainer
          heading={heading}
          subheading={subheading}
          onContinue={formik.handleSubmit}
          disableContinue={formik.isSubmitting}
          continueButtonType="submit"
          hideSkip
          nextStep={nextStep}
          error={
            error
              ? error
              : !formik.isValid && formik.submitCount > 0
              ? flattenErrors(formik.errors).join('. ')
              : null
          }
        >
          <NUXForm />
        </StepContainer>
      )}
    </Formik>
  );
};

export default StepProfile;
